<template>
  <div>
    <div v-if="isLoadChart" class="text-center block-plotly">
      <ProgressSpinner />
    </div>
    <div v-show="!isLoadChart" id="graph" class="block-plotly"></div>
    <ChartToolbar :is-load-chart="isLoadChart" :is-auto-update-chart="isAutoUpdateChart"
      :is-show-current-value="Boolean(dataTraces?.length)" :periods="periods" :hovermode="hovermode" :marker="marker"
      :propertyAll="propertyAll" @update:dateBegin="periods.dateBegin = $event"
      @update:dateBeginSQL="periods.dateBeginSQL = $event" @update:dateEnd="periods.dateEnd = $event"
      @update:dateEndSQL="periods.dateEndSQL = $event" @update:hovermode="hovermode = $event"
      @update:marker="marker = $event" @update:propertyAll="propertyAll = $event; prepareTrend();"
      @refresh="refreshChart" @get-data="getDataChart" @show-data="showData = true"
      @auto-update="onClickAutoUpdateChart" />
    <div class="chart-table">
      <DataTable :value="dataProperty" editMode="cell" selectionMode="single" sortField="typeName" :sortOrder="1"
        class="editable-cells-table p-datatable-sm p-datatable-gridlines" responsiveLayout="scroll" autoLayout
        :loading="!dataProperty" ref="tableTrend" scrollHeight="220px" scrollable>
        <template #empty> Записи не найдены. </template>
        <template #loading> Записи загружаются. Пожалуйста подождите. </template>
        <Column v-for="col of columns" :sortable="col.sortable ? col.sortable : false" :field="col.field"
          :header="col.header" :key="col.field" :bodyStyle="col.bodyStyle">
          <template #body="slotProps" v-if="col.dataType && col.editor">
            <component appendTo="body" v-model="slotProps.data[col.field]" v-bind:is="col.editor.type" autofocus
              v-bind="col.editor.attrList" :style="col.editor.style ? col.editor.style : ''"
              :optionLabel="col.editor.label ? col.editor.label : col.field"
              :optionValue="col.editor.value ? col.editor.value : col.field"
              @hide="changeColor(col.editor.type === 'ColorPicker', slotProps.data)">
            </component>
          </template>
          <template #body="slotProps" v-else-if="col.dataType && col.dataType.type === 'Checkbox'">
            <i :class="{
              'pi pi-check-circle': slotProps.data[col.field],
              'pi pi-times-circle': !slotProps.data[col.field],
            }" :style="slotProps.data[col.field] ? 'color:green' : ''"></i>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
  <Dialog v-model:visible="showData" :style="{ width: '50vw', maxWidth: '90vw' }" modal>
    <template #header>
      <i class="pi pi-database mr-3" :style="{ 'font-size': '1.5rem', color: 'green' }" />
      <div class="font-bold text-center" style="font-size: 20px">
        Данные
      </div>
    </template>
    <TabView style="min-height: 500px" @tab-change="$e => indexTemp = $e.index">
      <TabPanel v-for="(item, index) in dataTemp" :header="item.name" :key="item.name">
        <DataTable v-if="index === indexTemp" :value="item.data" :virtualScrollerOptions="{ itemSize: 28 }"
          class="editable-cells-table p-datatable-sm p-datatable-gridlines" scrollHeight="520px" scrollable>
          <template #empty> Записи не найдены. </template>
          <template #header>
            <Button label="Экспорт Excel" icon="pi pi-file-excel" @click="onExportExcel" severity="success"
              class="mr-3 "></Button></template>
          <Column v-for="col of columnsTemp" sortable :field="col.field" :header="col.header" :key="col.field"
            :bodyStyle="col?.bodyStyle">
          </Column>
        </DataTable>
      </TabPanel>
    </TabView>
    <template #footer>
      <div class="flex justify-content-end">
        <Button label="Ок" icon="pi pi-check" class="p-button-text" @click="showData = false" />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted, defineProps, defineEmits } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import ChartToolbar from './ChartToolbar.vue';
import { CHART_CONFIG, TABLE_COLUMNS, TABLE_TEMP_COLUMNS } from './chartConfig';
import { exportExcel } from '@/core/utils/ExportExcel';

const PlotlyLocale = require('plotly.js/lib/locales/ru.js');
const Plotly = require('plotly.js');

const store = useStore();
const route = useRoute();

// ===== затычка  =====
const emit = defineEmits([
  'setScript',
]);
const props = defineProps({
  scriptId: {
    type: Number,
    required: true,
  },
  interception: {
    type: Boolean,
    required: true,
  },
});
// ===== Реактивные данные =====
const currentFilters = ref({});
const propertyService = ref(null);
const isLoadChart = ref(false);
const isAutoUpdateChart = ref(false);
const intervalUpdateChart = ref(null);
const hovermode = ref('x unified');
const marker = ref('lines');
const propertyAll = ref(false);
const periods = reactive({
  dateBegin: null,
  dateBeginSQL: null,
  dateEnd: null,
  dateEndSQL: null,
});
const stopUpdateProperty = ref(false);
const layout = CHART_CONFIG.layout;
const columns = TABLE_COLUMNS;
const columnsTemp = TABLE_TEMP_COLUMNS;
const showData = ref(false);
const indexTemp = ref(0);
const dataProperty = ref([]);
const dataTraces = ref([]);
const dataTemp = ref([]);
const countStartAutoUpdate = ref(0);

// ===== Computed =====
const visibleLines = computed(() => {
  return dataProperty.value.filter(item => item.is_visible);
});

// ===== Вспомогательные функции =====
const formatDateTime = (date) => {
  return store.getters.formatSqlDateTime(date);
};

const formatChartDate = (date, format) => {
  return store.getters.typeFormat(date, format);
};

const showError = (error, source) => {
  store.commit('SET_ERROR', {
    er: error,
    source: ['TTMPropertyLogChartForm', source],
  });
};

const generateColor = (index) => {
  index++;
  const c1 = index % 2 ? 3 : 22;
  const c2 = index % 2 ? 19 : 9;
  const c3 = index % 2 ? 7 : 14;
  const color = ('0' + index * c1).slice(-2) + ('0' + index * c2).slice(-2) + ('0' + index * c3).slice(-2);
  return String(color);
};

// ===== Методы =====
const initializePlotly = () => {
  Plotly.register(PlotlyLocale);
  dataTraces.value = [];
  Plotly.newPlot('graph', dataTraces.value, layout, {
    locale: 'ru',
    scrollZoom: true,
  });
};

const initializeDates = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();

  periods.dateBegin = new Date(year, month, day - 1);
  periods.dateEnd = new Date(year, month, day, 23, 59, 59);

  periods.dateBeginSQL = formatDateTime(periods.dateBegin);
  periods.dateEndSQL = formatDateTime(periods.dateEnd);
};

const initializeService = () => {
  const { login, password } = store.getters;
  propertyService.value = store.getters.nameToService(
    'BaseService',
    login,
    password,
    'property_log',
    'trend'
  );
};

const changeColor = (start, data) => {
  store.dispatch('setPropertyColor', {
    facilityTypeId: route.params.facility_type_id,
    systemName: data.sysName,
    flangeNo: data.flangeNo,
    value: data.color,
  });
  if (!start || !data.is_visible) return;
  const traceIndex = dataTraces.value.findIndex(trace => trace.id === data.id);
  if (traceIndex === -1) return;
  const newColor = `#${data.color}`;
  if (newColor !== dataTraces.value[traceIndex].marker.color) {
    dataTraces.value[traceIndex].marker.color = newColor;
    refreshChart();
  }
};

const onClickAutoUpdateChart = () => {
  isAutoUpdateChart.value = !isAutoUpdateChart.value;
  if (isAutoUpdateChart.value) {
    startAutoUpdate();
  } else {
    clearUpdateInterval();
  }
};

const startAutoUpdate = () => {
  if (countStartAutoUpdate.value === 0) {
    clearLastValueChart();
  }
  countStartAutoUpdate.value++;
  intervalUpdateChart.value = setInterval(() => {
    getCurrValueDataChart();
  }, (store.getters.propertyUpdate * 2) * 1000);
  getCurrValueDataChart();
};

const clearUpdateInterval = () => {
  if (intervalUpdateChart.value) {
    clearInterval(intervalUpdateChart.value);
    intervalUpdateChart.value = null;
  }
};

const clearLastValueChart = () => {
  for (let index = 0; index < dataTraces.value.length; index++) {
    if (dataTraces.value[index].x?.length || dataTraces.value[index].y?.length) {
      dataTraces.value[index].x.pop();
      dataTraces.value[index].y.pop();
    }
  }
};

const getCurrValueDataChart = () => {
  const updateData = { x: [], y: [] };
  const indices = [];
  for (let index = 0; index < dataTraces.value.length; index++) {
    const property = store.getters.propertyAll.find(
      (item) => item.id === dataTraces.value[index].id
    );
    if (property) {
      const dateText = formatChartDate(new Date(property.dateProperty), 'date-time-tm');
      let value = null;
      if (property.outputValue?.value !== null && property.outputValue?.value !== undefined) {
        value = property.outputValue.value === true ? 1 : property.outputValue.value;
      }
      if (
        dataTraces.value[index].x?.length &&
        dataTraces.value[index].x[dataTraces.value[index].x.length - 1] === dateText
      ) {
        continue;
      }
      dataTraces.value[index].x.push(dateText);
      dataTraces.value[index].y.push(value);
      updateData.x.push([dateText]);
      updateData.y.push([value]);
      indices.push(index);
    }
    if (updateData.x.length > 0) {
      Plotly.extendTraces('graph', updateData, indices);
    }
  }
};

const getDataChart = async (isLoad = true) => {
  if (!visibleLines.value.length) {
    showError('Не выбрано ни одного свойства для отображения на графике', 'getDataChart');
    return;
  }

  if (!periods.dateBeginSQL || !periods.dateEndSQL) {
    return;
  }
  const duration = periods.dateEndSQL - periods.dateBeginSQL;
  if (duration > 200000000) {
    showError('Период не может превышать 2 месяца!', 'getDataChart');
    return;
  }

  try {
    isLoadChart.value = isLoad;
    countStartAutoUpdate.value = 0;
    await fetchChartData();
  } catch (error) {
    showError(error?.response ?? error, 'getDataChart');
  } finally {
    isLoadChart.value = false;
  }
};

const fetchChartData = async () => {
  const promises = visibleLines.value.map((line) => fetchLineData(line));
  await Promise.all(promises);
  prepareChartData(visibleLines.value);
};

const fetchLineData = async (line) => {
  try {
    const data = await propertyService.value.getData({
      property_id: line.id,
      facility_id: route.params.facility_id,
      date_begin: periods.dateBeginSQL,
      date_end: periods.dateEndSQL,
    });
    if (data.length) {
      line.data = data;
    }
  } catch (error) {
    showError(`Не удалось получить данные для ${line.typeName}:${line.flangeNo}:${error}`, 'fetchLineData');
    line.data = [];
  }
};

const prepareChartData = (lines) => {
  dataTemp.value = [];
  dataTraces.value = [];
  lines.forEach((line) => {
    const sortedData = line.data?.length
      ? [...line.data].sort((a, b) => a.property_log_date - b.property_log_date)
      : [];

    dataTemp.value.push(createTemp(line, sortedData));
    dataTraces.value.push(createTrace(line, sortedData));
  });
  refreshChart();
};

const createTemp = (line, sortedData) => {
  return {
    data: sortedData.map((item) => ({
      ...item,
      property_log_date: formatChartDate(new Date(item.property_log_date), 'date-time-gmt'),
    })),
    name: `Фл${line.flangeNo} ${line.typeName}`,
  };
};

const createTrace = (line, sortedData) => {
  const { x, y } = prepareTraceData(sortedData);
  return {
    x,
    y,
    type: 'scatter',
    name: `Фл${line.flangeNo} ${line.typeName}`,
    id: line.id,
    line: { width: 0.9 },
    marker: {
      color: `#${line.color}`,
      size: 3,
    },
    mode: marker.value,
  };
};

const prepareTraceData = (data) => {
  const x = [formatChartDate(periods.dateBegin, 'date-time-tm')];
  const y = [null];

  data.forEach((measurement) => {
    x.push(formatChartDate(new Date(measurement.property_log_date), 'date-time-chart'));
    y.push(measurement.property_outvalue);
  });

  x.push(formatChartDate(periods.dateEnd, 'date-time-tm'));
  y.push(null);

  return { x, y };
};

const refreshChart = () => {
  dataTraces.value.forEach((trace) => {
    trace.mode = marker.value;
  });

  layout.hovermode = hovermode.value;

  Plotly.newPlot('graph', dataTraces.value, layout, {
    locale: 'ru',
    scrollZoom: true,
  });

  if (!isAutoUpdateChart.value) {
    triggerAutoScale();
  }
};

const triggerAutoScale = () => {
  const autoScaleButton = document.querySelector('[data-title="Автоматическое шкалирование"]');
  if (autoScaleButton) {
    autoScaleButton.click();
  }
};

const prepareTrend = () => {
  const properties = propertyAll.value
    ? store.getters.propertyAll
    : store.getters.propertyAll.filter((item) => item.isVisibleForm);
  dataProperty.value = properties.map((item, index) => formatTrendItem(item, index));
};

const fetchTrend = () => {
  if (!store.getters.propertyAll || stopUpdateProperty.value) return;
  prepareTrend();
  if (dataProperty.value?.length) {
    stopUpdateProperty.value = true;
  }
};

const formatTrendItem = (item, index) => {
  const color = store.getters.propertyColor(
    route.params.facility_type_id,
    item.sysName,
    item.flangeNo
  );

  return {
    ...item,
    is_visible: false,
    value: parseValue(item.value),
    outputValue: parseValue(item.outputValue),
    color: color ? color : generateColor(index),
    date: item.dateProperty ? formatChartDate(new Date(item.dateProperty), 'date-time') : null,
  };
};

const parseValue = (valueObj) => {
  if (!valueObj || valueObj.value === undefined) return null;
  if (valueObj.valueClass === 'java.lang.Boolean') {
    return Boolean(valueObj.value);
  }
  return valueObj.value;
};

const onExportExcel = () => {
  exportExcel({
    fileName: `Тренд ${dataTemp.value[indexTemp.value]?.name || ''}.xlsx`,
    tableData: dataTemp.value[indexTemp.value]?.data || [],
    tableColumns: TABLE_TEMP_COLUMNS,
  });
};

onMounted(() => {
  initializePlotly();
  initializeDates();
  initializeService();
});

onUnmounted(() => {
  clearUpdateInterval();
});

// ===== Watch =====
watch(
  () => store.getters.propertyAll,
  (newVal) => {
    if (newVal) fetchTrend();
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
::v-deep(.p-colorpicker-preview) {
  height: 1.7rem !important;
}

.block-plotly {
  height: 560px;
  width: 125rem;
}

h3 {
  margin: 40px 0 0;
}

ul {
  list-style-type: none;
  padding: 0;
}

li {
  display: inline-block;
  margin: 0 10px;
}

a {
  color: #42b983;
}
</style>